/**
 * Check 1 — file format vs the publisher's accept window.
 *
 * COMPOSES, DOES NOT RESTATE. `isThreadsSupportedImageUrl` and
 * the Threads predicate is the SAME predicate
 * `shared/posting-readiness.ts` uses to block a publish (:579-591 and
 * :467-476), which is in turn what `schedule-post`, `process-scheduled-posts`
 * and `post-dispatcher` all consume. Re-implementing the rule here would
 * create a second source of truth that drifts from the one the publisher
 * actually enforces — a chip reading "passed" while publish is blocked.
 */
import { isPostingVideoUrl, platformDisplayName } from '../../../../shared/posting-readiness.ts';
import { isThreadsSupportedImageUrl } from '../../../../shared/threads-post-params.ts';
import type { PlacementRow } from '../../platform-matrix/placement-matrix.ts';
import type { MediaKind } from '../../platform-matrix/placement-resolution.ts';
import { setCropReencodeJpegFix } from '../fixes.ts';
import { CHECK_LABEL, type QualityCheck } from '../types.ts';

/**
 * Both helpers fail CLOSED on an extensionless URL (stated explicitly at
 * threads-post-params.ts:44-50). That posture is kept — but the copy says so
 * plainly rather than implying Mora read a format it could not read.
 */
function unreadableFormatDetail(platform: string): string {
  return `Mora could not read this file's format from its URL, so it is treating it as unsupported by ${platformDisplayName(platform)}.`;
}

/**
 * Whether the URL's LAST PATH SEGMENT carries a file extension. Deliberately
 * not a test against the whole URL: `https://cdn.example.com` would match a
 * naive `\.[a-z0-9]{2,5}$` on its ".com" and be reported as a readable format
 * it does not have.
 */
function hasReadableExtension(url: string): boolean {
  const path = url.split(/[?#]/)[0];
  const segment = path.slice(path.lastIndexOf('/') + 1);
  return /\.[a-z0-9]{2,5}$/i.test(segment);
}

export function checkFormat(input: {
  platform: string;
  mediaUrls: string[];
  mediaKind: MediaKind;
  hasAspectOverride: boolean;
  row: PlacementRow | undefined;
}): QualityCheck {
  const { platform, mediaUrls, mediaKind, hasAspectOverride, row } = input;
  const base = { id: 'format' as const, label: CHECK_LABEL.format };
  const name = platformDisplayName(platform);

  if (mediaKind === 'text' || mediaUrls.length === 0) {
    return { ...base, status: 'skipped', detail: 'No media to check.' };
  }

  const imageUrls = mediaUrls.filter((url) => !isPostingVideoUrl(url));

  // OVERRIDE-AWARE PASS. An aspect override makes the publish path re-encode
  // the image as JPEG before it ever reaches the platform — see
  // fixes.ts#setCropReencodeJpegFix for the three-line citation trail. This
  // branch is what makes the one-click fix observably work: without it the
  // row would still read "fail" after the user applied the only available
  // remedy.
  if (hasAspectOverride && imageUrls.length > 0) {
    return {
      ...base,
      status: 'pass',
      detail: `Mora re-encodes this image as JPEG when it publishes to ${name}.`,
    };
  }

  const platformKey = platform.toLowerCase().trim();

  if (platformKey === 'threads') {
    const bad = imageUrls.filter((url) => !isThreadsSupportedImageUrl(url));
    if (bad.length > 0) {
      const unreadable = bad.some((url) => !hasReadableExtension(url));
      return {
        ...base,
        status: 'fail',
        detail: unreadable
          ? unreadableFormatDetail(platform)
          : 'Threads rejects WebP images. Mora saved this one as .webp.',
        fix: setCropReencodeJpegFix(platform, row),
      };
    }
    return { ...base, status: 'pass', detail: 'Threads accepts this image (JPEG or PNG).' };
  }

  if (platformKey === 'tiktok') {
    // TikTok takes JPEG or WebP only, but Mora reads each photo's real bytes at
    // publish and converts it to a 1080 px JPEG, so the source format is never
    // a reason to fail here.
    return {
      ...base,
      status: 'pass',
      detail: 'Mora converts this image to JPEG for TikTok.',
    };
  }

  if (imageUrls.length === 0) {
    return {
      ...base,
      status: 'pass',
      detail: `${name} accepts the MP4 video Mora produces.`,
    };
  }

  if (platformKey === 'instagram' || platformKey === 'facebook') {
    // Meta's API takes JPEG for Instagram images and does not list WebP for
    // Facebook photos. Mora re-encodes every still image to JPEG when it
    // publishes to either, so the source format is never the problem.
    const nonJpeg = imageUrls.filter((url) => !/\.jpe?g(\?|#|$)/i.test(url.split(/[?#]/)[0]));
    return {
      ...base,
      status: 'pass',
      detail:
        nonJpeg.length > 0
          ? `${name} takes JPEG images. Mora converts this one to JPEG when it publishes.`
          : `${name} accepts this image (JPEG).`,
    };
  }

  return {
    ...base,
    status: 'pass',
    detail: `${name} accepts the formats Mora produces (JPEG, PNG and WebP).`,
  };
}
